import { useState, useEffect } from 'react';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { getAdminDocList } from '../../../services/admin-docs.service';
import { AdminDoc, AdminDocDivision } from '../../../models/admin-doc.model';
import { onError } from '../../../utils/error.util';

function divisionLabel(division: AdminDocDivision): string {
  switch (division) {
    case AdminDocDivision.OnboardingCCL: return 'CCL Onboarding';
    case AdminDocDivision.OnboardingTSF: return 'TSF Onboarding';
    case AdminDocDivision.CCL: return 'CCL';
    case AdminDocDivision.TSF: return 'TSF';
    case AdminDocDivision.Company: return 'Company';
    default: return division;
  }
}

const DIVISION_ORDER = [
  AdminDocDivision.OnboardingCCL,
  AdminDocDivision.OnboardingTSF,
  AdminDocDivision.CCL,
  AdminDocDivision.TSF,
  AdminDocDivision.Company,
];

export default function OnboardingContainer() {
  const [adminDocs, setAdminDocs] = useState<AdminDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    loadAdminDocs();
  }, []);

  async function loadAdminDocs() {
    try {
      const result = await getAdminDocList({ limit: 100 });
      setAdminDocs(
        result.items.filter((doc) => doc.documents.some((f) => f.isPublic && f.publicUrl))
      );
    } catch (e: any) {
      onError(e);
    } finally {
      setIsLoading(false);
    }
  }

  function handleCopy(url: string, key: string) {
    navigator.clipboard.writeText(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  }

  const grouped = DIVISION_ORDER
    .map((div) => ({ div, docs: adminDocs.filter((d) => d.division === div) }))
    .filter(({ docs }) => docs.length > 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom component="div" sx={{ fontWeight: 600 }}>
        Onboarding
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {isLoading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : adminDocs.length === 0 ? (
        <Typography color="text.secondary">No onboarding documents available</Typography>
      ) : (
        <Stack spacing={4}>
          {grouped.map(({ div, docs }) => (
            <Box key={div}>
              <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600 }}>
                {divisionLabel(div)}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2.5}>
                {docs.map((doc) => {
                  const publicFiles = doc.documents.filter((f) => f.isPublic && f.publicUrl);
                  return (
                    <Box key={doc.id}>
                      <Typography variant="subtitle2" component="div" sx={{ fontWeight: 600, mb: 0.75 }}>
                        {doc.name}
                      </Typography>
                      <Stack spacing={0.5}>
                        {publicFiles.map((f, i) => {
                          const copyKey = `${doc.id}-${i}`;
                          return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={copiedKey === copyKey ? 'Copied!' : 'Copy link'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopy(f.publicUrl!, copyKey)}
                                  sx={{ color: copiedKey === copyKey ? 'success.main' : 'text.secondary' }}
                                >
                                  <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Typography
                                component="a"
                                href={f.publicUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                color="primary"
                                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {f.label || f.fileName}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
